'use client';

import {forwardRef, useContext} from 'react';
import styled from '@emotion/styled';

import theme from 'theme';
import {SizeTokenValue} from 'common';
import {CalendarIcon} from 'icons';

import {Input, InputButton, Label} from 'Input';
import {DatepickerContext} from 'Datepicker';

import {useInputComponent} from './utils';

import type {InputInternalProps, InputProps, InputStyles} from 'Input';

const Container = styled.div`
  position: relative;
  flex-grow: 1;
  height: 100%;
`;

const valueStyle = ({isSmall, isFocused, isFilled, isLabeled}: InputStyles) => ({
  opacity: !isLabeled || isFocused || isFilled ? 1 : 0,
  minHeight: isSmall ? 32 : 48,
  paddingTop: isLabeled && !isSmall ? 14 : 0,
  textOverflow: !isFocused ? 'ellipsis' : undefined,
});

const Value = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0;
  outline: none;
  border: none;

  ${theme.typography.p1}
`;

const ValueItem = styled.span<{isCurrent: boolean} & Pick<InputStyles, 'isFilled' | 'isDisabled'>>`
  white-space: pre;
  color: ${props => {
    if (props.isDisabled) {
      return theme.colors.grey[40];
    }

    return theme.colors.grey[props.isFilled ? 100 : 60];
  }};

  span {
    background-color: ${props => (props.isCurrent ? theme.colors.accent[10] : 'transparent')};
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  border: 0;
  outline: none;
  opacity: 0;
`;

const InputComponent: React.FC<InputInternalProps> = props => {
  const {inputProps, style, qa} = props;

  const {step, componentProps, segments} = useInputComponent(inputProps);

  const items = segments.map(({label, end, value}, indx) => {
    const textContent = value.padEnd(label.length, label[0]);

    return (
      <ValueItem key={indx} isFilled={!!value} isCurrent={step === indx} isDisabled={style.isDisabled}>
        <span>{textContent}</span>
        {end}
      </ValueItem>
    );
  });

  return (
    <Container>
      <Label {...props} style={{...style, isFilled: style.isFocused || style.isFilled}} />
      <Value data-qa={`${qa}-value`} style={valueStyle(style)}>
        {items}
      </Value>
      <HiddenInput data-qa={qa} {...componentProps} autoComplete="off" />
    </Container>
  );
};

type DateInputProps = Omit<InputProps, 'value' | 'onChange'>;

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, ref) => {
  const {qa, size, disabled, readOnly, endAdornment: parentEndAdornment, ...restProps} = props;

  const {isOpen, setIsOpen, value} = useContext(DatepickerContext);

  const endAdornment = (
    <>
      {parentEndAdornment}
      <InputButton
        type="button"
        data-qa={`${qa}-toggle`}
        isDisabled={disabled || readOnly}
        isSmall={size === SizeTokenValue.Small}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarIcon size={size} />
      </InputButton>
    </>
  );

  const inputProps: InputProps = {
    ...restProps,

    size,
    disabled,
    readOnly,
    endAdornment,
    inputComponent: InputComponent,
    containerRef: ref,

    value: value[0] || value[1] ? 'filled' : '',
    onChange: () => {},

    qa: `${qa}-input`,
  };

  return <Input {...inputProps} />;
});
